/**
 * CORP//LLM — Game Client Entry Point
 * Router, State, Top-Bar, Navigation
 */

import { renderDashboard }  from './screens/dashboard';
import { renderMap }        from './screens/map';
import { closePlanMode } from './map/plan-ui';
import { renderRunners }    from './screens/runners';
import { renderEconomy }    from './screens/economy';
import { renderContacts }   from './screens/contacts';
import { renderDiplomacy }  from './screens/diplomacy';
import { renderPlayback }   from './screens/playback';
import { renderGazette }    from './screens/gazette';
import { renderLeaderboard } from './screens/leaderboard';
import { renderSettings }    from './screens/settings';
import { renderAdmin }       from './screens/admin';
import { renderLlm }         from './screens/llm';
import { fixture, setFixture, setMapData } from './data/fixture';
import type { ClientFixture, MapData } from './data/fixture';
import { GameSocket, resolveWSUrl } from './ws';
import { getJwt, clearJwt, handleCallback, parseJwt } from './auth';
import { renderLogin } from './screens/login';
import { renderHub } from './screens/hub';
import { setServerBase, getPreferences, fetchAPI, ApiError } from './api';
import { initAssetResolver, _setManifestForDev } from './data/AssetResolver';
import { warmupPortraitEngine } from './scene/PortraitEngine';
import { formatVersionGlobal, formatVersionTooltip } from './utils/version';
import type { VersionMetaLike } from './utils/version';
import { initChat, createChatPanel, toggleChat } from './chat';
import { initUpdates, onNotificationPush } from './screens/updates';
import { renderLobbyWaiting } from './screens/lobby-waiting';
import { showBriefing } from './screens/briefing';
import { refreshOnboarding, setOnboardingDismissed } from './screens/onboarding';
import { initEliminationWatch, checkEliminations } from './screens/elimination-watch';
import type { BriefingPayload } from './screens/briefing';
import { loadI18n, applyI18nToDOM, setLang, getLang, SUPPORTED_LANGS, t } from './i18n';
import { initConsoleCapture } from './screens/bug-report';
import { initErrorReporting } from './utils/error-reporter';

// ── State ──────────────────────────────────────────────────────────────────

export interface MockState {
  sessionId: string;
  corpName: string;
  cycle: number;
  cycleMax: number;
  deadlineHour: number;
  cleanCash: number;
  dirtyCash: number;
  netWorth: number;
  federalHeat: number;
  notifications: Notification[];
}

export interface Notification {
  level: 'crit' | 'warn' | 'info';
  text: string;
  sub?: string;
  screen?: string;
}

// Mutable state — populated from WS state_update before first render.
export const state: MockState = {
  sessionId:     '',
  corpName:      '',
  cycle:         0,
  cycleMax:      0,
  deadlineHour:  23,
  cleanCash:     0,
  dirtyCash:     0,
  netWorth:      0,
  federalHeat:   0,
  notifications: [],
};

function applyFixtureToState(): void {
  const p = fixture.player;
  state.corpName    = p.corpName;
  state.cycle       = p.cycle;
  state.cycleMax    = p.cycleMax;
  state.deadlineHour = fixture.deadlineHour ?? 22;
  state.cleanCash   = p.cleanCash;
  state.dirtyCash   = p.dirtyCash;
  state.netWorth    = p.netWorth;
  state.federalHeat = p.federalHeat;
  state.notifications = (fixture.notifications ?? []).map(n => ({
    level: n.level, text: n.text, sub: n.sub,
  }));
}

// ── Router ─────────────────────────────────────────────────────────────────

type ScreenId = 'dashboard' | 'map' | 'runners' | 'economy'
              | 'contacts' | 'diplomacy' | 'playback' | 'gazette' | 'leaderboard'
              | 'settings' | 'admin' | 'llm';

const RENDERERS: Partial<Record<ScreenId, () => HTMLElement>> = {
  dashboard:  renderDashboard,
  map:        renderMap,
  runners:    renderRunners,
  economy:    renderEconomy,
  contacts:   renderContacts,
  diplomacy:  renderDiplomacy,
  playback:   renderPlayback,
  gazette:    renderGazette,
  leaderboard: renderLeaderboard,
  settings:    renderSettings,
  admin:       renderAdmin,
  llm:         renderLlm,
};

let currentScreen: ScreenId = 'dashboard';

// ── Hash routing ──────────────────────────────────────────────────────────

export type { ScreenId };

type ParsedHash =
  | { mode: 'hub'; tab?: string }
  | { mode: 'game'; sessionId: string; screen: ScreenId }
  | { mode: 'api'; sessionId: string };

export function parseHash(hash = location.hash): ParsedHash {
  const raw = hash.replace(/^#/, '').split('?')[0];
  if (!raw || raw === 'hub') return { mode: 'hub' };
  if (raw.startsWith('hub/')) return { mode: 'hub', tab: raw.slice(4) };
  if (raw.startsWith('api/')) return { mode: 'api', sessionId: raw.slice(4) };

  const slashIdx = raw.indexOf('/');
  if (slashIdx === -1) {
    // Legacy bare screen name (e.g. #dashboard) — treat as game with active session
    return { mode: 'game', sessionId: state.sessionId, screen: raw as ScreenId };
  }
  const sessionId = raw.slice(0, slashIdx);
  const screen = raw.slice(slashIdx + 1) as ScreenId;
  return { mode: 'game', sessionId, screen };
}

export function nav(screen: ScreenId): void {
  const sid = state.sessionId;
  if (sid) {
    history.pushState(null, '', `#${sid}/${screen}`);
  }
  navigateTo(screen);
}
(window as any).nav = nav;

function navigateTo(screen: ScreenId): void {
  // Close plan mode when navigating away from map
  if (screen !== 'map') closePlanMode();

  currentScreen = screen;

  document.querySelectorAll('.nav-item').forEach(el => {
    const s = (el as HTMLElement).dataset.screen as ScreenId;
    el.classList.toggle('active', s === screen);
  });

  // Legacy #plan hash → redirect to map
  if ((screen as string) === 'plan') {
    screen = 'map' as ScreenId;
    currentScreen = screen;
    document.querySelectorAll('.nav-item').forEach(el => {
      el.classList.toggle('active', (el as HTMLElement).dataset.screen === 'map');
    });
  }

  // Render screen
  const container = document.getElementById('screen-container')!;
  container.innerHTML = '';
  const renderer = RENDERERS[screen];
  if (renderer) {
    container.appendChild(renderer());
  } else {
    container.innerHTML = `<div class="screen"><div class="screen-title">${screen}</div><p style="color:var(--text-mid); padding: 20px 0;">${t('main.screen_not_implemented')}</p></div>`;
  }
}

// ── Top Bar ────────────────────────────────────────────────────────────────

let topBarInited = false;

function showChrome(visible: boolean): void {
  const app = document.getElementById('app') as HTMLElement;
  app.classList.toggle('shell-mode', !visible);
}

function initTopBar(): void {
  showChrome(true);
  const el = (id: string) => document.getElementById(id);

  // Corp name
  const logo = document.querySelector('#topbar .logo') as HTMLElement;
  if (logo) logo.innerHTML = `${state.corpName.toUpperCase().replace(/ /g, '<span class="accent"> </span>')}`;

  (el('cycleVal') as HTMLElement).textContent = `${state.cycle}/${state.cycleMax}`;
  (el('cleanCash') as HTMLElement).textContent = formatCash(state.cleanCash);
  (el('dirtyCash') as HTMLElement).textContent = formatCash(state.dirtyCash);
  (el('netWorth') as HTMLElement).textContent = formatCash(state.netWorth);

  updateHeatBar(state.federalHeat);

  if (!topBarInited) {
    startCountdown();
    initNotifications();
    initUpdates();
    // Chat toggle button
    const chatBtn = document.getElementById('chatBtn');
    if (chatBtn) chatBtn.addEventListener('click', () => toggleChat());
    // Language toggle
    const langBtn = document.getElementById('langBtn') as HTMLButtonElement | null;
    if (langBtn) {
      langBtn.addEventListener('click', () => {
        const next = SUPPORTED_LANGS.find(l => l !== getLang()) ?? 'de';
        setLang(next);
        window.location.reload();
      });
    }
    topBarInited = true;
  } else {
    refreshNotifications();
  }
}

function updateHeatBar(heat: number): void {
  const bar   = document.getElementById('heatBar')  as HTMLElement;
  const badge = document.getElementById('heatBadge') as HTMLElement;

  let level: string;
  let color: string;
  if      (heat < 20) { level = 'GREEN';  color = 'var(--green)'; }
  else if (heat < 40) { level = 'YELLOW'; color = 'var(--amber)'; }
  else if (heat < 60) { level = 'ORANGE'; color = 'var(--orange)'; }
  else if (heat < 80) { level = 'RED';    color = 'var(--red)'; }
  else                { level = 'INDICT'; color = 'var(--magenta)'; }

  // Topbar heat is a donut dial: ring = conic-gradient filled to heat%, tinted
  // by tier; num shows the value. Level word kept on the dial title (hover) and
  // as a CSS class (tier colour / logic) rather than inline text.
  bar.style.background = `conic-gradient(${color} ${heat}%, rgba(255,255,255,0.07) 0)`;
  badge.textContent  = String(heat);
  badge.style.color  = color;
  badge.className    = `num heat-badge ${level}`;
  const dial = bar.parentElement;
  if (dial) dial.setAttribute('title', `Federal Heat: ${level} ${heat}`);
}

function startCountdown(): void {
  const timerEl = document.getElementById('countdownTimer') as HTMLElement;
  function tick() {
    const now = new Date();
    const deadline = new Date();
    deadline.setHours(state.deadlineHour, 0, 0, 0);
    if (deadline <= now) deadline.setDate(deadline.getDate() + 1);
    const diff = deadline.getTime() - now.getTime();
    const h = Math.floor(diff / 3_600_000);
    const m = Math.floor((diff % 3_600_000) / 60_000);
    const s = Math.floor((diff % 60_000) / 1_000);
    timerEl.textContent = `${pad(h)}:${pad(m)}:${pad(s)}`;
    if (diff < 3_600_000) timerEl.style.color = 'var(--red)';
    else timerEl.style.color = 'var(--cyan)';
  }
  tick();
  setInterval(tick, 1_000);
}

// ── Notifications ──────────────────────────────────────────────────────────

function buildNotifList(list: HTMLElement): void {
  list.innerHTML = '';
  for (const n of state.notifications) {
    const item = document.createElement('div');
    item.className = 'notif-item';
    item.innerHTML = `
      <div class="notif-dot ${n.level}"></div>
      <div>
        <div class="notif-text">${n.text}</div>
        ${n.sub ? `<div class="notif-sub">${n.sub}</div>` : ''}
      </div>`;
    if (n.screen) {
      const panel = document.getElementById('notifPanel') as HTMLElement;
      item.addEventListener('click', () => {
        navigateTo(n.screen as ScreenId);
        panel.classList.remove('open');
      });
    }
    list.appendChild(item);
  }
}

function initNotifications(): void {
  const badge = document.getElementById('notifBadge') as HTMLElement;
  const panel = document.getElementById('notifPanel') as HTMLElement;
  const list  = document.getElementById('notifList')  as HTMLElement;
  const btn   = document.getElementById('notifBtn')   as HTMLButtonElement;
  const close = document.getElementById('notifClose') as HTMLButtonElement;

  badge.textContent = String(state.notifications.length);
  badge.classList.toggle('visible', state.notifications.length > 0);
  buildNotifList(list);

  btn.addEventListener('click', () => panel.classList.toggle('open'));
  close.addEventListener('click', () => panel.classList.remove('open'));
}

function refreshNotifications(): void {
  const badge = document.getElementById('notifBadge') as HTMLElement;
  const list  = document.getElementById('notifList')  as HTMLElement;
  badge.textContent = String(state.notifications.length);
  badge.classList.toggle('visible', state.notifications.length > 0);
  buildNotifList(list);
}

// ── Nav wiring ─────────────────────────────────────────────────────────────

function initNav(): void {
  document.querySelectorAll('.nav-item[data-screen]').forEach(el => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      nav((el as HTMLElement).dataset.screen as ScreenId);
    });
  });

  // Hub link — return to session picker
  const hubLink = document.getElementById('nav-hub');
  if (hubLink) {
    hubLink.addEventListener('click', (e) => {
      e.preventDefault();
      goToHub();
    });
  }
}

// ── Hub navigation helper ─────────────────────────────────────────────────

function goToHub(): void {
  state.sessionId = '';
  state.corpName = '';
  sessionStorage.removeItem('corpllm_active_session');
  sessionStorage.removeItem('corpllm_active_corp');
  history.pushState(null, '', '#hub');
  location.reload();
}

// ── Boot screens ───────────────────────────────────────────────────────────

function showConnecting(): void {
  const container = document.getElementById('screen-container')!;
  container.innerHTML = `
    <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;gap:16px;color:var(--cyan);font-family:var(--mono);">
      <div style="font-size:1.5rem;letter-spacing:0.2em;">CORP//LLM</div>
      <div style="color:var(--text-mid);font-size:0.85rem;">${t('main.connecting')}<span id="dots"></span></div>
    </div>`;
  // Animated dots
  let d = 0;
  const dotsEl = document.getElementById('dots');
  const dotsTimer = setInterval(() => {
    if (!dotsEl || !document.contains(dotsEl)) { clearInterval(dotsTimer); return; }
    dotsEl.textContent = '.'.repeat((d++ % 3) + 1);
  }, 400);
}

// showSessionWaiting replaced by renderLobbyWaiting in lobby-waiting.ts

type ConnectErrorKind = 'timeout' | 'server_unreachable' | 'session_not_found' | 'session_ended';

function showConnectError(wsUrl: string, kind: ConnectErrorKind = 'timeout', detail?: string): void {
  const container = document.getElementById('screen-container')!;
  const isDev = import.meta.env.DEV;
  const isLocal = wsUrl.includes('localhost') || wsUrl.includes('127.0.0.1');

  const titles: Record<ConnectErrorKind, string> = {
    timeout: t('conn.title_timeout'),
    server_unreachable: t('conn.title_unreachable'),
    session_not_found: t('conn.title_session_gone'),
    session_ended: t('conn.title_session_ended'),
  };
  const descriptions: Record<ConnectErrorKind, string> = {
    timeout: t('conn.desc_timeout', { seconds: isDev ? '5' : '30' }),
    server_unreachable: t('conn.desc_unreachable'),
    session_not_found: t('conn.desc_session_gone'),
    session_ended: t('conn.desc_session_ended'),
  };

  const diagnostics: string[] = [];
  if (!getJwt()) diagnostics.push(t('conn.diag_no_jwt'));
  if (detail) diagnostics.push(detail);
  if ((kind === 'timeout' || kind === 'server_unreachable') && !isLocal) {
    diagnostics.push(t('conn.diag_vpn'));
  }

  const diagHtml = diagnostics.length > 0
    ? diagnostics.map(d => `<div style="color:var(--amber);font-size:0.8rem;text-align:left">- ${d}</div>`).join('')
    : '';

  const showRetry = kind === 'timeout' || kind === 'server_unreachable';

  container.innerHTML = `
    <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;gap:12px;color:var(--red);font-family:var(--mono);max-width:600px;margin:0 auto;">
      <div style="font-size:2rem;">&#x26A0;</div>
      <div style="font-size:1.1rem;font-weight:bold;">${titles[kind]}</div>
      <div style="color:var(--text-mid);font-size:0.85rem;">${descriptions[kind]}</div>
      ${diagnostics.length > 0 ? `<div style="width:100%;padding:10px;background:rgba(231,57,70,0.08);border:1px solid rgba(231,57,70,0.2);border-radius:4px;margin:4px 0">
        ${diagHtml}
        <div style="color:var(--text-dim);font-size:0.7rem;margin-top:6px;word-break:break-all">${wsUrl}</div>
      </div>` : ''}
      <div style="display:flex;gap:12px;margin-top:8px">
        <button id="err-hub" style="padding:8px 24px;background:none;border:1px solid var(--cyan);color:var(--cyan);cursor:pointer;font-family:var(--mono);">${t('conn.back_to_hub')}</button>
        ${showRetry ? `<button id="err-retry" style="padding:8px 24px;background:none;border:1px solid var(--red);color:var(--red);cursor:pointer;font-family:var(--mono);">RETRY</button>` : ''}
      </div>
    </div>`;

  container.querySelector('#err-hub')?.addEventListener('click', goToHub);
  container.querySelector('#err-retry')?.addEventListener('click', () => location.reload());
}

// ── Core game logic ─────────────────────────────────────────────────────────

function applyAndRender(firstRender: boolean): void {
  applyFixtureToState();
  initTopBar();
  // Wire help link → wiki
  const helpLink = document.getElementById('helpLink');
  if (helpLink && !helpLink.dataset.wired) {
    helpLink.dataset.wired = '1';
    helpLink.addEventListener('click', (e) => {
      e.preventDefault();
      window.open('https://wiki.corpllm.io', '_blank');
    });
  }

  // Wire bug report button
  const bugLink = document.getElementById('bugReportLink');
  if (bugLink && !bugLink.dataset.wired) {
    bugLink.dataset.wired = '1';
    bugLink.addEventListener('click', (e) => {
      e.preventDefault();
      import('./screens/bug-report').then(m => m.openBugReporter());
    });
  }
  // Always-visible version footer (every screen, incl. shell-mode). See updateVersionFooter().
  updateVersionFooter();
  if (firstRender) {
    const parsed = parseHash();
    const screen = parsed.mode === 'game' && parsed.screen in RENDERERS ? parsed.screen : 'dashboard';
    navigateTo(screen);
  } else {
    navigateTo(currentScreen);
  }
}

function showToast(text: string, durationMs = 3500): void {
  const el = document.createElement('div');
  el.textContent = text;
  el.style.cssText = 'position:fixed;bottom:80px;left:50%;transform:translateX(-50%);background:var(--bg);border:1px solid var(--cyan-dim);color:var(--text-hi);font-family:var(--mono);font-size:12px;padding:6px 14px;border-radius:3px;z-index:9999;pointer-events:none;opacity:1;transition:opacity 0.4s';
  document.body.appendChild(el);
  setTimeout(() => { el.style.opacity = '0'; setTimeout(() => el.remove(), 400); }, durationMs);
}

// ── Version Check ─────────────────────────────────────────────────────────

let versionCheckInterval: ReturnType<typeof setInterval> | null = null;
let lastKnownServerVersion = localStorage.getItem('corpllm_server_version') ?? '';

// Cached /api/meta payload used by the topbar version widget. Fetched once
// per session on first fixture render; refreshed opportunistically by
// checkClientVersion (runs every 5 minutes) when the server version drifts.
let topbarVersionMeta: VersionMetaLike | null = null;
let topbarVersionMetaFetching = false;

async function fetchTopbarVersionMeta(): Promise<VersionMetaLike> {
  const jwt = getJwt();
  const res = await fetch('/api/meta', {
    headers: jwt ? { 'Authorization': `Bearer ${jwt}` } : {},
  });
  if (!res.ok) throw new Error(`meta ${res.status}`);
  const json = await res.json() as VersionMetaLike;
  return json;
}

// Populate the always-visible #versionInfo footer with "client vX (sha) ·
// server vY (sha)". Client is always known; server comes from cached /api/meta
// (or fixture.engineVersion / the localStorage cache before meta resolves).
// Safe to call repeatedly and pre-game (login/hub) — server is simply omitted
// until known. The /api/meta fetch (bot version, coverage) is kicked off once
// per session when a JWT is available and surfaced in the hover tooltip.
function updateVersionFooter(): void {
  const vi = document.getElementById('versionInfo');
  if (!vi) return;
  let cachedServer: string | undefined;
  try { cachedServer = localStorage.getItem('corpllm_server_version') ?? undefined; } catch { /* SSR/test */ }
  const meta: VersionMetaLike = topbarVersionMeta ?? { serverVersion: fixture.engineVersion || cachedServer };
  vi.textContent = formatVersionGlobal(meta);
  vi.title = formatVersionTooltip(topbarVersionMeta ?? meta);
  if (!topbarVersionMetaFetching && !topbarVersionMeta && getJwt()) {
    topbarVersionMetaFetching = true;
    fetchTopbarVersionMeta().then(m => {
      topbarVersionMeta = m;
      topbarVersionMetaFetching = false;
      vi.textContent = formatVersionGlobal(m);
      vi.title = formatVersionTooltip(m);
    }).catch(() => { topbarVersionMetaFetching = false; });
  }
}

function showUpdateBanner(message: string, showRefresh: boolean): void {
  if (document.getElementById('update-banner')) return;
  const banner = document.createElement('div');
  banner.id = 'update-banner';
  banner.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:10000;background:#1a1a2e;border-bottom:1px solid var(--cyan-dim,#00f5d466);padding:8px 16px;display:flex;align-items:center;justify-content:center;gap:12px;font-family:var(--mono,monospace);font-size:12px;color:var(--text-hi,#e0e0e0)';
  const text = document.createElement('span');
  text.textContent = message;
  banner.appendChild(text);
  if (showRefresh) {
    const btn = document.createElement('button');
    btn.textContent = 'REFRESH';
    btn.style.cssText = 'background:none;border:1px solid var(--cyan,#00f5d4);color:var(--cyan,#00f5d4);font-family:var(--mono,monospace);font-size:11px;padding:3px 12px;cursor:pointer;letter-spacing:0.1em';
    btn.addEventListener('click', () => location.reload());
    banner.appendChild(btn);
  }
  const close = document.createElement('button');
  close.textContent = '✕';
  close.style.cssText = 'background:none;border:none;color:var(--text-dim,#888);font-size:14px;cursor:pointer;margin-left:4px';
  close.addEventListener('click', () => banner.remove());
  banner.appendChild(close);
  document.body.appendChild(banner);
}

async function checkClientVersion(): Promise<void> {
  try {
    const res = await fetch('/version.json?_=' + Date.now());
    if (!res.ok) return;
    const data = await res.json();
    if (data.version && data.version !== __APP_VERSION__) {
      showUpdateBanner(t('version.client_update', { version: data.version }), true);
    }
  } catch { /* offline or dev — ignore */ }
}

function checkServerVersion(engineVersion: string): void {
  if (!engineVersion) return;
  if (lastKnownServerVersion && lastKnownServerVersion !== engineVersion) {
    showUpdateBanner(t('version.server_update', { version: engineVersion }), false);
  }
  lastKnownServerVersion = engineVersion;
  localStorage.setItem('corpllm_server_version', engineVersion);
}

function startVersionChecks(): void {
  if (versionCheckInterval) return;
  checkClientVersion();
  versionCheckInterval = setInterval(checkClientVersion, 5 * 60_000);
}

function showLoginScreen(error?: string): void {
  const container = document.getElementById('screen-container')!;
  container.innerHTML = '';
  container.appendChild(renderLogin(error));
}

async function startGame(sessionId: string, corpName: string, serverBaseArg?: string): Promise<void> {
  void serverBaseArg; // reserved for future use
  state.sessionId = sessionId;
  // Persist session so F5 reconnects instead of going back to Hub
  sessionStorage.setItem('corpllm_active_session', sessionId);
  sessionStorage.setItem('corpllm_active_corp', corpName);
  history.replaceState(null, '', `#${sessionId}/dashboard`);
  showConnecting();
  // Prime the bot-corps catalog so elimination events can resolve names to
  // i18n keys for the epitaph modal. Fires in parallel with the WS connect —
  // if the first state_update arrives before the catalog, epitaphs for that
  // tick are skipped silently (they resume once the catalog is cached).
  void initEliminationWatch();

  const jwt = getJwt();
  const params = new URLSearchParams();
  if (sessionId && sessionId !== 'dev') params.set('session', sessionId);
  if (jwt) params.set('token', jwt);
  const paramStr = params.toString();
  const wsUrl = resolveWSUrl() + (paramStr ? '?' + paramStr : '');

  // ── Pre-check: validate session exists before opening WebSocket ──
  if (sessionId && sessionId !== 'dev') {
    try {
      const detail = await fetchAPI<{ id: string; status: string }>(`/api/sessions/${encodeURIComponent(sessionId)}`);
      if (detail.status === 'finished' || detail.status === 'archived') {
        showConnectError(wsUrl, 'session_ended');
        return;
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 404) {
          showConnectError(wsUrl, 'session_not_found');
          return;
        }
        // 401/403 — let WS try anyway (maybe token issue, WS has its own auth)
      } else {
        // Network error — server unreachable
        showConnectError(wsUrl, 'server_unreachable');
        return;
      }
    }
  }

  const socket = new GameSocket(wsUrl);
  let gotState = false;

  const wsTimeoutMs = import.meta.env.DEV ? 5_000 : 30_000;

  const timeout = setTimeout(() => {
    if (gotState) return;
    showConnectError(wsUrl, 'timeout');
  }, wsTimeoutMs);

  socket.on<ClientFixture>('state_update', (data) => {
    if (!data) return;
    clearTimeout(timeout);
    console.debug('[state_update] plan entries:', data.player?.plan?.length ?? 0,
      'day:', (data as any).day ?? '?', 'runners:', data.player?.runners?.length ?? 0);
    if (!gotState && data.engineVersion) {
      console.info(`[CORP//LLM] server ${data.engineVersion}`);
      checkServerVersion(data.engineVersion);
      startVersionChecks();
    }
    setFixture(data);
    applyAndRender(!gotState);
    checkEliminations(data);
    refreshOnboarding();
    gotState = true;
  });

  socket.on<MapData>('map_data', (data) => {
    if (!data) return;
    setMapData(data);
  });

  // Server signals: session exists but hasn't started yet.
  // Show the full lobby waiting room with player list, chat, and ready button.
  // state_update will arrive once the session transitions to running.
  socket.on<{ sessionId: string; status: string; deadlineHour?: number }>('session_lobby', (payload) => {
    if (gotState) return;
    clearTimeout(timeout);
    const container = document.getElementById('screen-container')!;
    container.innerHTML = '';
    container.appendChild(renderLobbyWaiting(socket, sessionId, payload?.deadlineHour ?? 22));
  });

  // Show morning briefing after each tick resolves (state_update always precedes tick_complete)
  socket.on<BriefingPayload>('tick_complete', (payload) => {
    if (!payload || !gotState) return;
    showBriefing(payload, (screen) => nav(screen as ScreenId));
  });

  socket.on<{ playerName: string }>('plan_submitted', (payload) => {
    if (payload?.playerName) showToast(t('main.plan_submitted', { name: payload.playerName }));
  });

  // Player join/leave events — dispatch to lobby waiting room if visible
  socket.on<{ corpName: string; accentColor: string; playerName: string }>('player_joined', (payload) => {
    if (!payload) return;
    const lobbyRoot = document.querySelector('.lw-root');
    if (lobbyRoot) {
      lobbyRoot.dispatchEvent(new CustomEvent('lobby-player-joined', { detail: payload }));
    }
  });

  socket.on<{ corpName: string; playerName: string }>('player_left', (payload) => {
    if (!payload) return;
    const lobbyRoot = document.querySelector('.lw-root');
    if (lobbyRoot) {
      lobbyRoot.dispatchEvent(new CustomEvent('lobby-player-left', { detail: payload }));
    }
  });

  socket.on<{ id?: string; severity?: string; title?: string; global?: boolean }>('notification_new', (payload) => {
    onNotificationPush(payload);
  });

  socket.on<{ playerName: string; readyCount: number; totalPlayers: number; readyCorps: string[] }>('player_ready', (payload) => {
    if (!payload) return;
    // Update fixture readiness state for live UI updates
    if (fixture.readyPlayers !== undefined) {
      fixture.readyPlayers = payload.readyCorps ?? [];
      fixture.totalPlayers = payload.totalPlayers;
      // Check if current player's corp is in the ready list
      fixture.playerReady = payload.readyCorps?.includes(state.corpName) ?? false;
    }
    if (payload.playerName) showToast(t('main.player_ready', { name: payload.playerName, ready: String(payload.readyCount), total: String(payload.totalPlayers) }));
    // Update dashboard readiness panel if visible
    const readyPanel = document.getElementById('ready-panel');
    if (readyPanel) {
      const event = new CustomEvent('readiness-update', { detail: payload });
      readyPanel.dispatchEvent(event);
    }
    // Update lobby waiting room if visible
    const lobbyRoot = document.querySelector('.lw-root');
    if (lobbyRoot) {
      lobbyRoot.dispatchEvent(new CustomEvent('lobby-player-ready', { detail: payload }));
    }
  });

  // Wire up chat (WS events + panel)
  initChat(socket);
  const app = document.getElementById('app')!;
  if (!document.getElementById('chatPanel')) {
    app.appendChild(createChatPanel());
  }

  socket.connect();

  window.addEventListener('popstate', () => {
    const parsed = parseHash();
    if (!gotState) return;
    if (parsed.mode === 'hub' || parsed.mode === 'api') { location.reload(); return; }
    navigateTo((parsed.screen && parsed.screen in RENDERERS) ? parsed.screen : 'dashboard');
  });
}

async function showLobby(serverBase: string): Promise<void> {
  return new Promise((resolve) => {
    const container = document.getElementById('screen-container')!;
    container.innerHTML = '';
    const hub = renderHub(serverBase, (sessionId, corpName) => {
      startGame(sessionId, corpName, serverBase);
      resolve();
    }, (sessionId) => {
      showChrome(false);
      container.innerHTML = '';
      import('./screens/api-console').then(m => {
        container.appendChild(m.renderApiConsole(sessionId, serverBase));
      });
      history.pushState(null, '', `#api/${sessionId}`);
    });
    container.appendChild(hub);
  });
}

// ── Boot ───────────────────────────────────────────────────────────────────

async function boot(): Promise<void> {
  // Start capturing console logs before anything else
  initConsoleCapture();

  // #751 — report unhandled errors to the server (throttled, PII-free)
  initErrorReporting();

  console.info(`[CORP//LLM] client ${__APP_VERSION__}`);

  // Load i18n before anything renders
  await loadI18n();
  applyI18nToDOM();

  // Show client (+ cached server) version in the footer immediately — before
  // login/shell-mode screens render, so the version is visible on every screen.
  updateVersionFooter();

  initNav();

  // Apply saved font size from settings
  const savedFontSize = localStorage.getItem('corpllm_font_size');
  if (savedFontSize) document.documentElement.style.fontSize = savedFontSize + 'px';

  // Determine server base URL
  const serverProto = location.protocol === 'https:' ? 'https:' : 'http:';
  const serverHost = import.meta.env.VITE_SERVER_HOST ?? 'localhost';
  const serverPort = import.meta.env.VITE_SERVER_PORT ?? '8080';
  const serverBase = `${serverProto}//${serverHost}:${serverPort}`;
  setServerBase(serverBase);

  // Handle OAuth callback (if ?code= in URL)
  if (location.search.includes('code=')) {
    const ok = await handleCallback(serverBase);
    if (!ok) { showLoginScreen(t('auth.failed')); return; }
  }

  // In PROD mode, check JWT
  if (import.meta.env.PROD) {
    const existingJwt = getJwt();
    if (!existingJwt) { showLoginScreen(); return; }
    const payload = parseJwt(existingJwt);
    if (!payload || payload.exp * 1000 < Date.now()) {
      clearJwt();
      showLoginScreen(t('auth.expired'));
      return;
    }
  }

  // Initialize asset resolver (fetches CDN manifest from corpllm-server) and
  // warm up portrait engine. Both depend on auth in PROD; in DEV the server
  // bypasses auth so they work too.
  //
  // Failure here is SOFT in both modes: we log a warning and continue so
  // non-asset screens (Hub, Dashboard, Settings, Chat, Map) still work even
  // if the manifest endpoint is unreachable (e.g. server not yet deployed
  // with the new endpoints, or transient outage). Asset-using screens
  // (PortraitEngine, character-preview, RecipeResolver…) will surface their
  // own errors when the user navigates to them.
  const assetEnv = import.meta.env.PROD ? 'prod' : 'staging';
  try {
    await initAssetResolver(assetEnv);
    warmupPortraitEngine().catch(err => console.warn('[boot] portrait warmup:', err));
  } catch (err) {
    console.warn('[boot] asset resolver init failed — continuing without asset CDN:', err);
  }

  // Apply server-saved preferences (accent color, font size) early so the
  // UI renders with the correct theme before the Hub or Settings screen loads.
  if (getJwt()) {
    getPreferences()
      .then(prefs => {
        if (prefs.accentColor) {
          document.documentElement.style.setProperty('--cyan', prefs.accentColor);
          document.documentElement.style.setProperty('--cyan-dim', prefs.accentColor + 'aa');
        }
        // client#240 — arm the first-session guidance once we know whether
        // the player already dismissed it (server-persisted flag).
        setOnboardingDismissed(!!prefs.onboardingDismissed);
      })
      .catch(() => { /* preferences unavailable — use defaults */ });
  }

  // Show admin nav item for admin users
  const adminNav = document.getElementById('nav-admin');
  if (adminNav) {
    const j = getJwt();
    if (j && parseJwt(j)?.admin) adminNav.style.display = '';
  }

  // Reconnect to active session on F5 (skip Hub)
  const parsed = parseHash();
  if (parsed.mode === 'api' && parsed.sessionId) {
    showChrome(false);
    const container = document.getElementById('screen-container')!;
    container.innerHTML = '';
    const { renderApiConsole } = await import('./screens/api-console');
    container.appendChild(renderApiConsole(parsed.sessionId, serverBase));
    return;
  }
  if (parsed.mode === 'game' && parsed.sessionId) {
    const corpFallback = sessionStorage.getItem('corpllm_active_corp') ?? '';
    startGame(parsed.sessionId, corpFallback, serverBase);
    return;
  }
  // Fallback: sessionStorage for legacy bookmarks
  const activeSession = sessionStorage.getItem('corpllm_active_session');
  const activeCorp = sessionStorage.getItem('corpllm_active_corp');
  if (activeSession && activeCorp) {
    startGame(activeSession, activeCorp, serverBase);
    return;
  }

  // No session — show Hub
  if (!location.hash.startsWith('#hub')) {
    history.replaceState(null, '', '#hub');
  }
  startVersionChecks();
  await showLobby(serverBase);
}

// ── Utils ──────────────────────────────────────────────────────────────────

export function formatCash(n: number): string {
  return '$' + n.toLocaleString('de-DE');
}

export function pad(n: number): string {
  return String(n).padStart(2, '0');
}

// ── Dev render-harness (E2E / screenshot-based UI analysis) ─────────────────
// A Playwright addInitScript (or harness.html) sets window.__FUI_HARNESS = true
// BEFORE this module evaluates, which skips boot() — no WebSocket, no auth, no
// network side effects — and instead exposes window.__harness so the e2e driver
// can render any single screen against the committed test fixture. It reuses
// the real RENDERERS, setFixture()/setMapData() and the topbar wiring, so what
// gets screenshotted is exactly the production render path minus the live
// server. See e2e/ for the capture + autonomous-analysis pipeline.

interface HarnessRenderOpts {
  lang?: string;        // 'de' | 'en' — locale to render with
  tab?: string;         // optional in-screen tab element id to click after render
  fixtureUrl?: string;  // override fixture (state variants); defaults to /data/fixture.json
}

async function harnessRenderScreen(screen: string, opts: HarnessRenderOpts = {}): Promise<void> {
  const lang = opts.lang ?? 'de';
  setLang(lang);
  await loadI18n(lang);
  applyI18nToDOM();

  // AssetResolver is normally initialized in boot(); here boot() is skipped, so
  // inject a Proxy-backed manifest that yields a (dead) URL for ANY asset id
  // instead of throwing. Asset-using screens then render with broken/empty
  // images rather than crashing. The dead URLs point at a non-existent local
  // path, so no network fetch to the real CDN happens.
  _setManifestForDev({
    version: 0, env: 'staging', cdnBase: '', builtAt: '',
    assetCount: 0, totalBytes: 0,
    assets: new Proxy({}, {
      get: (_t, id) => ({ type: 'glb', r2Key: `harness/${String(id)}.dev`, sha256: '', size: 0 }),
      has: () => true,
    }) as unknown as Record<string, never>,
  });

  const fx = await (await fetch(opts.fixtureUrl ?? '/data/fixture.json')).json();
  setFixture(fx as ClientFixture);

  // The Map screen needs per-session map data (normally a WS map_data event).
  if (screen === 'map') {
    try {
      const [cityGraph, wb] = await Promise.all([
        fetch('/data/city_graph.json').then(r => r.json()),
        fetch('/data/world_buildings.json').then(r => (r.ok ? r.json() : { buildings: [] })),
      ]);
      // world_buildings.json is { meta, buildings: [...] }; MapData wants the array.
      const worldBuildings = Array.isArray(wb) ? wb : (wb.buildings ?? []);
      setMapData({ cityGraph, worldBuildings } as MapData);
    } catch { /* map degrades to an untextured graph — acceptable for analysis */ }
  }

  showChrome(true);
  applyFixtureToState();
  initTopBar();
  navigateTo(screen as ScreenId);

  if (opts.tab) {
    const tabEl = document.getElementById(opts.tab) as HTMLElement | null;
    if (tabEl) tabEl.click();
  }
}

if (!(window as unknown as { __FUI_HARNESS?: boolean }).__FUI_HARNESS) {
  boot();
} else {
  (window as unknown as { __harness?: unknown }).__harness = {
    renderScreen: harnessRenderScreen,
    setLang,
  };
  (window as unknown as { __harnessReady?: boolean }).__harnessReady = true;
}
